<template>
  <div
    class="relative mx-6 my-4 flex flex-col rounded-md border border-gray-200"
  >
    <div class="flex items-center justify-between" :class="backgroundColor">
      <div class="flex w-full px-6 py-4">
        <div class="flex min-w-40 shrink items-center justify-between">
          <icon-base
            v-if="showLoadingSpinner"
            :icon="IconLoadingCircle"
            class="mr-4 h-5 w-5 animate-spin rounded-full text-primary"
            view-box="0 0 20 20"
          />
          <div v-if="isPreviewSupported" data-cy="file-preview">
            <img
              v-if="file.imagePreviewUrl"
              class="max-h-6 max-w-6"
              :src="file.imagePreviewUrl"
              :alt="t('COMMON.PREVIEW')"
              loading="lazy"
            />
            <icon-base
              v-else
              class="shrink-0"
              :icon="fileType"
              height="16"
              width="16"
              viewBox="0 0 46 46"
            />
          </div>
          <div class="flex min-w-0 shrink items-start pl-4">
            <span
              :title="file.name"
              class="mr-2 min-w-0 shrink grow text-sm font-medium break-words text-gray-800"
            >
              {{ file.name }}
            </span>
            <span v-if="!file.failed">{{ formattedFileSize }}</span>
            <span v-else class="text-red-500">
              {{ $t("DEALS.DOCUMENTS.UPLOAD_FAILED") }}
            </span>
          </div>
        </div>
        <div class="flex shrink grow items-center justify-end">
          <icon-base
            v-if="file.failed && allowRetry"
            class="cursor-pointer"
            icon="reload"
            height="24"
            width="24"
            data-cy="retry-button"
            @click="retry"
          />
          <icon-base
            v-if="!file.uploaded"
            class="ml-4 cursor-pointer"
            :icon="IconClear"
            height="14"
            width="14"
            data-cy="uploaded-document-remove"
            @click="cancel"
          />
        </div>
      </div>
    </div>
    <div
      v-if="
        !!typeDropdownOptions?.length &&
        !uploadTriggered &&
        !file.uploaded &&
        !file.failed &&
        !fileFailed
      "
      class="flex gap-5 p-4"
    >
      <stip-dropdown-categories
        class="document-upload-dd w-0"
        data-cy="document-type-dropdown"
        :placeholder="$t('STIPS.SELECT_FILE_TYPE')"
        v-model="selectedDocumentType"
        @change="handleDocumentTypeChange"
      />
      <lf-dropdown
        v-if="showOwnersDropdown"
        class="document-upload-dd w-0"
        name="owners"
        :options="owners"
        :placeholder="$t('DEALS.DOCUMENTS.SELECT_OWNER')"
        :value="Object.keys(owners || [])?.[0]"
        full-width
        @change="handleDocumentOwnerChange"
      />
      <lf-dropdown
        v-if="file.documentType === SIGNED_DOC_FILE_TYPE || signedDocOnly"
        class="document-upload-dd w-0"
        name="product_type"
        :options="productTypeDropdownOptions"
        :placeholder="$t('DEALS.DOCUMENTS.SELECT_PRODUCT_TYPE')"
        :value="file.productTypeId"
        show-clear-filter
        full-width
        @change="handleDocumentProductTypeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { SIGNED_DOC_FILE_TYPE } from "@/helpers/constants";
import { formatFileSize, getDocumentFileType } from "@/helpers/formatting";

import StipDropdownCategories from "@/components/deals/StipDropdownCategories.vue";
import IconClear from "@/components/icons/IconClear.vue";
import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";

import type { FileWBlob, FinancialProduct } from "@/models/common";
import type { ProductType } from "@/models/options";
import type { PropType } from "vue";

const emit = defineEmits([
  "optionSelected",
  "fileCancelled",
  "fileRetry",
  "productTypeSelected",
  "ownerSelected",
]);

const props = defineProps({
  file: {
    type: Object as PropType<FileWBlob>,
    required: true,
  },
  typeDropdownOptions: {
    type: Array,
    default: () => [],
  },
  uploadTriggered: {
    type: Boolean,
    required: true,
  },
  ownerId: {
    type: String,
  },
  owners: {
    type: Object as PropType<Record<string, string>>,
    required: false,
  },
  signedDocOnly: {
    type: Boolean,
    default: false,
  },
  allowRetry: {
    type: Boolean,
  },
  showLoadingSpinner: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();
const { getters } = useStore();

const selectedDocumentType = ref();
const fileFailed = ref(false);

const fileType = computed(() => getDocumentFileType(props.file.name));

const isPreviewSupported = computed(() => fileType.value !== "heic");

const productTypeDropdownOptions = computed<
  Record<ProductType, FinancialProduct>
>(() => getters["options/productTypes"]);

const formattedFileSize = computed(() => {
  const { unit, size } = formatFileSize(props.file.size);
  return t(`DEALS.DOCUMENTS.UPLOAD_FILE_SIZE_${unit}`, {
    size: size,
  });
});

const showOwnersDropdown = computed(
  () =>
    (props.file.documentType === SIGNED_DOC_FILE_TYPE || props.signedDocOnly) &&
    !props.ownerId &&
    Object.keys(props.owners || [])?.length > 1
);

const backgroundColor = computed(() => {
  if (props.file.failed) {
    return "bg-red-100";
  }
  if (props.file.uploaded) {
    return "bg-green-100";
  }
  return "bg-blue-100";
});

const retry = () => {
  fileFailed.value = true;
  emit("fileRetry", props.file.id);
};

const cancel = () => emit("fileCancelled", props.file.id);

const handleDocumentTypeChange = () => {
  emit("optionSelected", props.file.id, selectedDocumentType);
};

const handleDocumentProductTypeChange = (option: string) =>
  emit("productTypeSelected", props.file.id, option);

const handleDocumentOwnerChange = (option: string) =>
  emit("ownerSelected", props.file.id, option);
</script>
